import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Trash2, Shield, ChevronRight, ChevronLeft, Anchor, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { BOARDING } from "@/constants/testIds";

const STEP_LABELS = ["Identity", "Signal", "Mandate", "Aesthetic Vault", "Boarding Deposit"];

const emptyForm = {
  full_name: "", email: "", phone: "",
  tiktok_handle: "", instagram_handle: "", youtube_handle: "",
  follower_count: "", engagement_rate: "",
  video_link: "", niche: "", narrative: "",
  portfolio: [],
};

export const BoardingForm = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [paymentMode, setPaymentMode] = useState(null); // {mode, amount, currency} or null

  useEffect(() => {
    if (open) {
      setStep(0);
      setForm(emptyForm);
      api.get("/payments/mode")
        .then((r) => setPaymentMode(r.data))
        .catch(() => setPaymentMode({ mode: "unknown" }));
    }
  }, [open]);
  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validators = [
    () => (form.full_name && form.email && form.phone) || "Identity is incomplete. Provide name, email, and phone.",
    () => ((form.tiktok_handle || form.instagram_handle || form.youtube_handle) && form.follower_count && form.engagement_rate)
      || "Provide at least one social handle plus follower count and engagement.",
    () => (form.video_link && form.niche && form.narrative.length >= 20)
      || "Mandate requires video link, niche, and a 20+ char narrative.",
    () => (form.portfolio.length >= 3 && form.portfolio.length <= 5)
      || "Aesthetic Vault: upload 3 to 5 high-fidelity images.",
    () => true,
  ];

  const next = () => {
    const ok = validators[step]();
    if (ok !== true) { toast.error(ok); return; }
    setStep((s) => Math.min(s + 1, STEP_LABELS.length - 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (form.portfolio.length + files.length > 5) {
      toast.error("Maximum 5 portfolio images."); return;
    }
    for (const file of files) {
      if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
        toast.error(`${file.name}: only JPEG / PNG / WebP accepted.`); continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      setUploadingCount((c) => c + 1);
      try {
        const { data } = await api.post("/uploads/aesthetic-vault", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm((f) => ({
          ...f,
          portfolio: [...f.portfolio, {
            id: data.id,
            url: `${API_BASE.replace(/\/api$/, "")}${data.url}`,
            previewUrl: URL.createObjectURL(file),
          }],
        }));
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
      } finally {
        setUploadingCount((c) => c - 1);
      }
    }
  };

  const removeImage = (id) => setForm((f) => ({ ...f, portfolio: f.portfolio.filter((p) => p.id !== id) }));

  const submit = async () => {
    if (validators[3]() !== true) { toast.error("Aesthetic Vault requires 3 to 5 images."); setStep(3); return; }
    setSubmitting(true);
    try {
      const payload = {
        application: {
          full_name: form.full_name, email: form.email, phone: form.phone,
          tiktok_handle: form.tiktok_handle, instagram_handle: form.instagram_handle, youtube_handle: form.youtube_handle,
          follower_count: form.follower_count, engagement_rate: form.engagement_rate,
          video_link: form.video_link, niche: form.niche, narrative: form.narrative,
          portfolio: form.portfolio.map((p) => p.id),
        },
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/applications/start", payload);
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.error("Failed to start Boarding Deposit checkout.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-end md:items-center justify-center overflow-y-auto"
      data-testid={BOARDING.modal}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}>
      <motion.div
        className="glass relative w-full md:w-[860px] max-w-full md:max-h-[92vh] my-0 md:my-8 overflow-y-auto"
        style={{ borderRadius: 0 }}
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.35 }}
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid={BOARDING.closeBtn}
          className="absolute top-4 right-4 text-white/60 hover:text-[#C9A227] z-10" aria-label="Close">
          <X size={22} />
        </button>

        <div className="px-6 md:px-10 pt-10 pb-6 border-b border-[#C9A227]/15">
          <div className="flex items-center gap-3 mb-3">
            <Shield className="text-[#C9A227]" size={18} />
            <span className="label-gold">Admiral Commission · Boarding Protocol</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tight text-white">
            Initiate <span className="gold-shimmer">Boarding</span>
          </h2>
          <p className="text-[#A0A0A0] text-sm mt-2 max-w-xl">
            5-stage intake. All data is encrypted. The Grand Sovereign is the sole reader of the Command Roster.
          </p>
          <div className="mt-6 flex items-center gap-1" data-testid={BOARDING.stepIndicator}>
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="flex-1 flex flex-col items-start gap-2">
                <div className={`h-[2px] w-full ${i <= step ? "bg-[#C9A227]" : "bg-white/10"} transition-colors duration-500`} />
                <div className={`text-[10px] tracking-[0.2em] uppercase ${i === step ? "text-[#C9A227]" : "text-white/40"}`}>
                  {String(i + 1).padStart(2, "0")} · {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 md:px-10 py-8 min-h-[340px]">
          <AnimatePresence mode="wait">
            <motion.div key={step}
              initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.3 }} className="space-y-5">
              {step === 0 && (
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label className="label-gold">Full Name</label>
                    <input className="siren-input" placeholder="Captain Surname" value={form.full_name}
                      onChange={(e) => set("full_name", e.target.value)} data-testid={BOARDING.fieldFullName}/>
                  </div>
                  <div>
                    <label className="label-gold">Email</label>
                    <input type="email" className="siren-input" placeholder="admiral@pacificsiren.com" value={form.email}
                      onChange={(e) => set("email", e.target.value)} data-testid={BOARDING.fieldEmail}/>
                  </div>
                  <div>
                    <label className="label-gold">Phone</label>
                    <input className="siren-input" placeholder="+1 808 …" value={form.phone}
                      onChange={(e) => set("phone", e.target.value)} data-testid={BOARDING.fieldPhone}/>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="label-gold">TikTok Handle</label>
                    <input className="siren-input" placeholder="@yoursignal" value={form.tiktok_handle}
                      onChange={(e) => set("tiktok_handle", e.target.value)} data-testid={BOARDING.fieldTiktok}/>
                  </div>
                  <div>
                    <label className="label-gold">Instagram Handle</label>
                    <input className="siren-input" placeholder="@yoursignal" value={form.instagram_handle}
                      onChange={(e) => set("instagram_handle", e.target.value)} data-testid={BOARDING.fieldInstagram}/>
                  </div>
                  <div>
                    <label className="label-gold">YouTube Channel</label>
                    <input className="siren-input" placeholder="@yourchannel" value={form.youtube_handle}
                      onChange={(e) => set("youtube_handle", e.target.value)} data-testid={BOARDING.fieldYoutube}/>
                  </div>
                  <div>
                    <label className="label-gold">Combined Follower Count</label>
                    <input className="siren-input" placeholder="e.g. 142K" value={form.follower_count}
                      onChange={(e) => set("follower_count", e.target.value)} data-testid={BOARDING.fieldFollowerCount}/>
                  </div>
                  <div>
                    <label className="label-gold">Engagement Rate</label>
                    <input className="siren-input" placeholder="e.g. 4.2%" value={form.engagement_rate}
                      onChange={(e) => set("engagement_rate", e.target.value)} data-testid={BOARDING.fieldEngagementRate}/>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <label className="label-gold">60-Second Vision Video (link)</label>
                    <input className="siren-input" placeholder="https://… TikTok / IG Reel / YouTube unlisted"
                      value={form.video_link} onChange={(e) => set("video_link", e.target.value)} data-testid={BOARDING.fieldVideoLink}/>
                  </div>
                  <div>
                    <label className="label-gold">Niche Specification</label>
                    <input className="siren-input" placeholder="e.g. Pacific barrier skincare for tropical climates"
                      value={form.niche} onChange={(e) => set("niche", e.target.value)} data-testid={BOARDING.fieldNiche}/>
                  </div>
                  <div>
                    <label className="label-gold">Narrative · Why do you command the Pacific territory?</label>
                    <textarea className="siren-input" rows={6}
                      placeholder="Your sovereign claim. Minimum 20 characters."
                      value={form.narrative} onChange={(e) => set("narrative", e.target.value)} data-testid={BOARDING.fieldNarrative}/>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <ImageIcon className="text-[#C9A227] mt-1" size={20} />
                    <div>
                      <h3 className="text-white text-lg font-bold uppercase tracking-wide">Aesthetic Vault</h3>
                      <p className="text-[#A0A0A0] text-sm">
                        Upload <span className="text-[#C9A227] font-bold">3 to 5</span> high-fidelity images:
                        skincare results, brand style, ritual proof. JPEG / PNG / WebP up to 8MB each.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {form.portfolio.map((p) => (
                      <div key={p.id} className="relative aspect-square overflow-hidden border border-[#C9A227]/20 group">
                        <img src={p.previewUrl} alt="portfolio" className="w-full h-full object-cover" />
                        <button onClick={() => removeImage(p.id)}
                          className="absolute top-2 right-2 bg-black/70 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    {form.portfolio.length < 5 && (
                      <label className="aspect-square border border-dashed border-[#C9A227]/40 flex flex-col items-center justify-center cursor-pointer hover:bg-[#C9A227]/5 transition-colors" data-testid={BOARDING.fieldPortfolioUpload}>
                        <Upload className="text-[#C9A227]" size={22}/>
                        <span className="text-[#C9A227] text-[10px] tracking-[0.2em] uppercase mt-2 font-bold">
                          {uploadingCount > 0 ? "Uploading…" : "Add Image"}
                        </span>
                        <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={handleUpload} />
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-white/40">{form.portfolio.length}/5 uploaded · minimum 3 required.</p>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <Anchor className="text-[#C9A227]" size={20}/>
                    <span className="label-gold">Final Step · Boarding Deposit</span>
                  </div>
                  <div className="glass p-6 border border-[#C9A227]/40">
                    <div className="flex items-baseline justify-between">
                      <span className="text-white/60 uppercase tracking-widest text-xs">Boarding Deposit</span>
                      <span className="text-4xl font-black gold-shimmer">$99.00</span>
                    </div>
                    <div className="hairline my-5" />
                    <ul className="space-y-2 text-sm text-white/80">
                      <li>• Reserves your seat in the Search for the Siren</li>
                      <li>• Issues your unique <span className="text-[#C9A227] font-bold">Sovereign Raffle Ticket</span></li>
                      <li>• Activates your public candidate profile + leaderboard slot</li>
                      <li>• Refundable if you are not shortlisted</li>
                    </ul>
                  </div>
                  <p className="text-xs text-white/50">
                    {paymentMode?.mode === "live" ? (
                      <span data-testid="boarding-live-notice" className="text-[#C9A227] font-bold">
                        ⚡ LIVE PAYMENT MODE · Pressing the button below redirects you to Stripe and your card will be charged <span className="gold-shimmer">$99 USD</span> in real funds. Refundable per the Charter if you are not shortlisted.
                      </span>
                    ) : paymentMode?.mode === "test" ? (
                      <span data-testid="boarding-test-notice">
                        Pressing the button below redirects you to Stripe (test mode). Use card 4242 4242 4242 4242 with any future date and CVC.
                      </span>
                    ) : (
                      <span>Pressing the button below redirects you to Stripe Checkout.</span>
                    )}
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="px-6 md:px-10 pb-8 pt-4 border-t border-[#C9A227]/15 flex items-center justify-between gap-3">
          <button onClick={back} disabled={step === 0} data-testid={BOARDING.backBtn}
            className="btn-outline-gold text-xs disabled:opacity-30">
            <ChevronLeft size={16} className="inline -mt-0.5" /> Back
          </button>
          {step < STEP_LABELS.length - 1 ? (
            <button onClick={next} className="btn-gold text-xs" data-testid={BOARDING.nextBtn}>
              Advance <ChevronRight size={16} className="inline -mt-0.5"/>
            </button>
          ) : (
            <button onClick={submit} className="btn-gold text-xs" disabled={submitting} data-testid={BOARDING.submitBtn}>
              {submitting ? "Routing to Stripe…" : "Pay $99 · Lock Commission"}
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};
